<script setup lang="ts">
export interface BaseCheckboxProps {
  /**
   * Defines if input is disabled or not.
   */
  disabled?: boolean;
  /**
   * Defines the label value of the input.
   */
  label?: string;
  /**
   * Required. Used to create id's for the input/label.
   */
  name: string;
  /**
   * Default to v-model
   */
  modelValue: boolean;
}

const checkboxRef = ref();
const props = defineProps<BaseCheckboxProps>();
const emit = defineEmits(["update:modelValue"]);
const onUpdateValue = (event: any) => emit("update:modelValue", event.target.checked);
</script>

<template>
  <div class="baseCheckbox" :class="{ baseCheckbox__disabled: props.disabled }">
    <div class="baseCheckbox__fieldWrap">
      <input
        :id="`checkbox-${props.name}`"
        ref="checkboxRef"
        :checked="props.modelValue"
        :disabled="props.disabled"
        class="baseCheckbox__field"
        type="checkbox"
        @change="onUpdateValue"
      >
      <IconCheck
        v-if="props.modelValue"
        class="baseCheckbox__iconCheck"
        :height="12"
        :width="12"
        fill="currentColor"
        @click="checkboxRef.click()"
      />
    </div>
    <slot name="before_label" @click.prevent="checkboxRef.click()" />
    <label v-if="props.label" class="baseCheckbox__label cursor-pointer" :for="`checkbox-${props.name}`" @click.prevent="checkboxRef.click()">
      {{ props.label }}
    </label>
  </div>
</template>

<style lang="scss">
.baseCheckbox {
  @apply relative flex items-center gap-2;

  --baseCheckbox__checkmark_color: #ffffff;
  --baseCheckbox__label_color: 'initial';
  --baseCheckbox__field_background: transparent;
  --baseCheckbox__field_border: 1px solid var(--base-secondary-color);
  --baseCheckbox__field_borderRadius: 4px;
  --baseCheckbox__field_border-checked: 1px solid var(--baseCheckbox__field_backgroundColor-checked);
  --baseCheckbox__field_backgroundColor-checked: var(--base-primary-color);

  .baseCheckbox__fieldWrap {
    @apply relative;
  }

  .baseCheckbox__iconCheck {
    @apply h-3 w-3 absolute top-1 left-1 cursor-pointer;

    color: var(--baseCheckbox__checkmark_color);
  }

  .baseCheckbox__field {
    @apply h-5 w-5
    appearance-none focus:outline-none cursor-pointer
    transition duration-200 align-top float-left
    bg-transparent bg-no-repeat bg-center bg-contain;

    border-radius: var(--baseCheckbox__field_borderRadius);
    // box-shadow: 0 11px 10px 0 rgba(0, 0, 0, 0.3);
    border: var(--baseCheckbox__field_border);
    background: var(--baseCheckbox__field_background);

    &:checked {
      background-color: var(--baseCheckbox__field_backgroundColor-checked);
      border: var(--baseCheckbox__field_border-checked);
    }
  }

  .baseCheckbox__label {
    @apply inline-block text-xs sm:text-base font-medium;

    color: var(--baseCheckbox__label_color);
  }

  //   &.baseCheckbox__disabled,
  &.baseCheckbox__disabled .baseCheckbox__label,
  &.baseCheckbox__disabled .baseCheckbox__field,
  &.baseCheckbox__disabled .baseCheckbox__iconCheck {
    @apply cursor-not-allowed opacity-75;
  }
}
</style>
